import React, { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import CrisisAlertIcon from "@mui/icons-material/CrisisAlert";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AttractionsIcon from "@mui/icons-material/Attractions";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import Typography from "@mui/material/Typography";
import { useSnackbar } from "../../context/snackbar.context.js";
import moment from "moment";
import { Box, Button, Chip, Divider, Stack, Grid } from "@mui/material";
import UserCard from "../../components/cards/user.card.js";
import TeamCard from "../../components/cards/team.card.js";
import "@xyflow/react/dist/style.css";
import { useState } from "react";
import { getUsersLight } from "../../api/user.api";
import {
  generateMatches,
  getMatches,
  getTournament,
  reGenerateTeams,
  setStatus,
  kick,
  reset,
} from "../../api/tournament.api.js";
import { useAuth } from "../../context/auth.context.js";
import { getTeams } from "../../api/team.api.js";
import KoMatchesComponent from "./ko.matches.js";
import RoundRobinMatchesComponent from "./roundrobin.matches.js";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import WinnerCard from "../../components/cards/winner.card.js";
import { useWebSocket } from "../../context/websocket.context.js";
import SelectGroupWinnerModal from "../../components/modals/selectGroupWinner.modal.js";
import { ReactFlowProvider } from "@xyflow/react";
import StatusChip from "../../components/chips/status.chip.js";

const TournamentPage = () => {
  const auth = useAuth();
  const location = useLocation();
  const [tournament, setTournament] = useState(location.state.tournament);
  const [participants, setParticipants] = useState([]);
  const [matches, setMatches] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const websocket = useWebSocket();
  const showSnackbar = useSnackbar();

  useEffect(() => {
    if (!websocket || !websocket.message) {
      return;
    }
    if (
      (websocket.message.Type === "Tournament" &&
        websocket.message.Id === tournament.id) ||
      (websocket.message.Type === "Match" &&
        matches.find((match) => match.id === websocket.message.Id))
    ) {
      getTournament(tournament.id).then((response) =>
        setTournament(response.data),
      );
    }
  }, [websocket]);

  const handleClose = () => {
    setOpenModal(false);
  };

  const handleOpen = () => {
    setOpenModal(true);
  };

  const showUserCards = useCallback((tournament) => {
    return (
      tournament.participantMode === "Single" ||
      (tournament.participantMode === "RandomTeams" &&
        tournament.status !== "Started" &&
        tournament.status !== "Running" &&
        tournament.status !== "Closed") ||
      (tournament.participantMode === "SkillbasedTeams" &&
        tournament.status !== "Started" &&
        tournament.status !== "Running" &&
        tournament.status !== "Closed")
    );
  }, []);

  const showTeamCards = useCallback((tournament) => {
    return (
      tournament.participantMode === "CustomTeams" ||
      (tournament.participantMode === "RandomTeams" &&
        (tournament.status === "Started" ||
          tournament.status === "Running" ||
          tournament.status === "Closed")) ||
      (tournament.participantMode === "SkillbasedTeams" &&
        (tournament.status === "Started" ||
          tournament.status === "Running" ||
          tournament.status === "Closed"))
    );
  }, []);

  useEffect(() => {
    const catchParticipantsAndMatches = (tournament) => {
      if (showUserCards(tournament)) {
        getUsersLight(tournament.participantIds).then((response) => {
          setParticipants(response.data);
          catchMatches(tournament, response.data);
        });
      }
      if (showTeamCards(tournament)) {
        getTeams(tournament.participantIds).then((response) => {
          setParticipants(response.data);
          catchMatches(tournament, response.data);
        });
      }
      return [];
    };

    const catchMatches = (tournament) => {
      if (
        tournament.status === "Started" ||
        tournament.status === "Running" ||
        tournament.status === "Closed"
      ) {
        getMatches(tournament.id).then((response) => {
          setMatches(response.data);
        });
      }
    };

    if (tournament.id) {
      catchParticipantsAndMatches(tournament);
    }
  }, [refresh, tournament, showUserCards, showTeamCards]);

  const isAdminAndHost = () => {
    return (
      auth &&
      auth.user &&
      auth.user.role === "Admin" &&
      tournament.admin === auth.user.id
    );
  };

  const canMatchesBeGenerated = () => {
    return isAdminAndHost() && tournament.status === "Started";
  };

  const canTeamsBeGenerated = () => {
    return (
      isAdminAndHost() &&
      tournament.status === "Started" &&
      (tournament.participantMode === "RandomTeams" ||
        tournament.participantMode === "SkillbasedTeams")
    );
  };

  const canRun = () => {
    return (
      isAdminAndHost() && tournament.status === "Started" && matches.length > 0
    );
  };

  const canStart = () => {
    return (
      isAdminAndHost() &&
      tournament.status === "Open" &&
      tournament.participantIds.length >= tournament.participantCountMin
    );
  };

  const canReset = () => {
    return (
      isAdminAndHost() &&
      (tournament.status === "Started" || tournament.status === "Running")
    );
  };

  const handleGenerateMatches = () => {
    generateMatches(tournament.id)
      .then(() => {
        setRefresh((prev) => !prev);
        showSnackbar("Generate Matches successfully!", "success");
      })
      .catch((error) => {
        showSnackbar("Failed to generate matches!", "error");
        console.log(error);
      });
  };

  const handleGenerateTeams = () => {
    reGenerateTeams(tournament.id)
      .then(() => {
        setRefresh((prev) => !prev);
        showSnackbar("Generate Teams successfully!", "success");
      })
      .catch((error) => {
        showSnackbar("Failed to generate teams!", "error");
        console.log(error);
      });
  };

  const handleReset = () => {
    reset(tournament.id)
      .then(() => {
        setRefresh((prev) => !prev);
        showSnackbar("Reset successful!", "success");
      })
      .catch((error) => {
        showSnackbar("Reset failed!", "error");
        console.log(error);
      });
  };

  const handleNextStatus = (status) => {
    setStatus(tournament.id, status)
      .then(() => {
        setRefresh((prev) => !prev);
        showSnackbar("Status updated successfully!", "success");
      })
      .catch((error) => {
        console.log(error);
        showSnackbar("Failed to update status!", "error");
      });
  };

  const refreshData = useCallback(() => {
    setRefresh((prev) => !prev);
  }, []);

  const handleKick = (evnt, participantId) => {
    if (evnt.ctrlKey) {
      evnt.stopPropagation();
      kick(tournament.id, participantId)
        .then(() => showSnackbar("Participant kicked!", "success"))
        .catch(() => showSnackbar("Participant could not be kicked!", "error"));
    }
  };

  return (
    <div className="dashboard-container">
      <Box
        sx={{ display: "flex", flexDirection: "row", alignItems: "flex-start" }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {tournament.isMainTournament && (
              <MilitaryTechIcon color="primary" sx={{ fontSize: 24, mr: 1 }} />
            )}
            <Typography variant="h3" gutterBottom color="primary">
              {tournament.name}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography sx={{ color: "text.secondary", mr: 3 }}>
                {tournament.game.name}
              </Typography>
              {tournament.game.category === "Shooter" && (
                <CrisisAlertIcon sx={{ fontSize: 16, mr: 1 }} />
              )}
              {tournament.game.category === "Racing" && (
                <DirectionsCarIcon sx={{ fontSize: 16, mr: 1 }} />
              )}
              {tournament.game.category === "Strategy" && (
                <AccountBalanceIcon sx={{ fontSize: 16, mr: 1 }} />
              )}
              {tournament.game.category === "Other" && (
                <AttractionsIcon sx={{ fontSize: 16, mr: 1 }} />
              )}
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {tournament.game.category}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <StatusChip status={tournament.status} />

              <Chip
                label={tournament.participantMode}
                variant="outlined"
                size="small"
              />

              <Chip label={tournament.system} variant="outlined" size="small" />
            </Stack>
          </Box>
          <Typography
            variant="body"
            sx={{
              color: "text.secondary",
              fontSize: 12,
              maxWidth: "400px",
            }}
          >
            {tournament.description}
          </Typography>

          <Typography sx={{ color: "text.secondary", fontSize: 12, mt: 2 }}>
            Start: {moment(tournament.start).format("DD.MM.YYYY HH:mm")}
          </Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
            Participants: {tournament.participantIds.length} /{" "}
            {tournament.participantCountMax}
          </Typography>
          {tournament.prize && (
            <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
              Prize: {tournament.prize}
            </Typography>
          )}
          <Typography>
            Host:{" "}
            {
              participants.find(
                (participant) => participant.id == tournament.admin,
              )?.nickname
            }
          </Typography>

          <Stack direction="row" spacing={1}>
            {canMatchesBeGenerated() && (
              <Button variant="contained" onClick={handleGenerateMatches}>
                Generate Matches
              </Button>
            )}
            {canTeamsBeGenerated() && (
              <Button variant="contained" onClick={handleGenerateTeams}>
                Regenerate Teams
              </Button>
            )}
            {canReset() && (
              <Button variant="contained" onClick={handleReset}>
                Reset Tournament
              </Button>
            )}
            {canRun() && (
              <Button variant="contained" onClick={() => handleNextStatus(3)}>
                Run
              </Button>
            )}
            {canStart() && (
              <Button variant="contained" onClick={() => handleNextStatus(2)}>
                Start
              </Button>
            )}
          </Stack>
        </Box>
        <Box sx={{ ml: 10, mt: 4 }}>
          <WinnerCard tournament={tournament} />
        </Box>
      </Box>

      <br />
      <Divider />

      <Typography variant="h4" gutterBottom>
        Participants
      </Typography>
      {showUserCards(tournament) && participants.length > 0 && (
        <Grid container spacing={1}>
          {participants.map((participant) => (
            <Grid
              key={participant.id}
              xs={12}
              sm={6}
              md={4}
              onClick={(e) => handleKick(e, participant.id)}
            >
              <UserCard user={participant} />
            </Grid>
          ))}
        </Grid>
      )}

      {showTeamCards(tournament) && participants.length > 0 && (
        <Grid container spacing={1}>
          {participants.map((participant) => (
            <Grid
              key={participant.id}
              xs={12}
              sm={6}
              md={4}
              onClick={(e) => handleKick(e, participant.id)}
            >
              <TeamCard team={participant} />
            </Grid>
          ))}
        </Grid>
      )}

      <br />
      <Divider />

      {(tournament.status === "Started" ||
        tournament.status === "Running" ||
        tournament.status === "Closed") && (
        <>
          <Typography variant="h4" gutterBottom>
            Matches
          </Typography>
          <div
            style={{
              width: "100%",
              height: "600px",
              minWidth: 0,
              position: "relative",
            }}
          >
            {tournament.system === "KO" && (
              <KoMatchesComponent
                refresh={refreshData}
                matches={matches}
                participants={participants}
              />
            )}
            {tournament.system === "RoundRobin" && (
              <RoundRobinMatchesComponent
                refresh={refreshData}
                matches={matches}
                participants={participants}
              />
            )}
            {tournament.system === "GroupKO" && (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <Box>
                    <Typography variant="h5" gutterBottom>
                      Group 1
                    </Typography>
                    <RoundRobinMatchesComponent
                      refresh={refreshData}
                      matches={matches.filter(
                        (match) => match.name === "group1",
                      )}
                      participants={participants.filter((participant) =>
                        matches
                          .filter((match) => match.name === "group1")
                          .some((match) =>
                            match.participants
                              .map((matchParticipant) => matchParticipant.id)
                              .includes(participant.id),
                          ),
                      )}
                    />
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box>
                    <Typography variant="h5" gutterBottom>
                      Group 2
                    </Typography>
                    <RoundRobinMatchesComponent
                      refresh={refreshData}
                      matches={matches.filter(
                        (match) => match.name === "group2",
                      )}
                      participants={participants.filter((participant) =>
                        matches
                          .filter((match) => match.name === "group2")
                          .some((match) =>
                            match.participants
                              .map((matchParticipant) => matchParticipant.id)
                              .includes(participant.id),
                          ),
                      )}
                    />
                  </Box>
                </Box>
                {matches &&
                  matches
                    .filter(
                      (match) =>
                        match.name === "group1" || match.name === "group2",
                    )
                    .every((match) => match.status === "Closed") &&
                  matches
                    .filter(
                      (match) =>
                        match.name === "semifinal1" ||
                        match.name === "semifinal2",
                    )
                    .every((match) => match.participants.length === 0) && (
                    <Button variant="contained" onClick={handleOpen}>
                      Select Winners Group Stage
                    </Button>
                  )}
                {openModal && (
                  <SelectGroupWinnerModal
                    aria-modal
                    open={openModal}
                    participants={participants}
                    handleClose={handleClose}
                    tournamentId={tournament.id}
                  />
                )}
                <Divider sx={{ mt: 1, mb: 1 }} />
                <Typography variant="h4">Finals</Typography>

                <div
                  style={{
                    width: "100%",
                    height: "600px",
                    minWidth: 0,
                    position: "relative",
                  }}
                >
                  {tournament &&
                    tournament.system === "GroupKO" &&
                    matches &&
                    matches.length > 0 &&
                    participants &&
                    participants.length > 0 && (
                      <ReactFlowProvider>
                        <KoMatchesComponent
                          refresh={refreshData}
                          matches={matches.filter(
                            (match) =>
                              match.name == "final" ||
                              match.name == "semifinal1" ||
                              match.name == "semifinal2",
                          )}
                          participants={participants}
                        />
                      </ReactFlowProvider>
                    )}
                </div>
              </Box>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TournamentPage;
